/* Page to fetch all ProTracker elements data
 * Contains the ProTrackers component to which it passes the data 
 * Contains component ProTrackerCreate */
import React, { useEffect, useState } from "react"
import useDepartmentPostsDepartments from "../../hooks/useDepartmentPostsDepartments"
import Form from "react-bootstrap/Form"
import Col from "react-bootstrap/Col"
import Row from "react-bootstrap/Row"
import Pagination from "react-bootstrap/Pagination"
import Container from "react-bootstrap/Container"
import { Dropdown } from "react-bootstrap";
import styles from "../../styles/Indexes.module.css"
import { axiosInstance, axiosReq } from "../../api/axiosDefaults"
import btnStyles from "../../styles/Button.module.css"
import { useRedirect } from "../../hooks/Redirect"
import { Button } from "react-bootstrap"
import { useLocation } from "react-router-dom"
import useHostName from "../../hooks/useHostName"
import { useCurrentUser } from "../../contexts/CurrentUserContext"
import { CLIENT_PROGRAM_HOSTNAME } from "../../utils/config"
import toast from "react-hot-toast";

import Information from "./info/Information"
import ProTrackerCreate from "./ProTrackerCreate"
import ProTracker from "./ProTracker"
import Asset from "../../components/Asset"
import NoResults from "../../assets/no-results.png"
import appStyles from "../../App.module.css"

const ProTrackerPage = ({ setShowTracker }) => {
  const host = useHostName()
  const location = useLocation()
  const { departments: departmentList } = useDepartmentPostsDepartments()
  const currentUser = useCurrentUser()
  useRedirect()
  const [proTrackers, setproTrackers] = useState({ results: [] })
  const [proTrackersAll, setProTrackersAll] = useState({ results: [] })
  // eslint-disable-next-line
  const [error, setErrors] = useState({})
  const [showInfo, setShowInfo] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [query, setQuery] = useState("")
  const [selectedDepartment, setSelectedDepartment] = useState("")
  const [selectedLevel, setSelectedLevel] = useState("")
  const [hasUserTriggeredFetch, setHasUserTriggeredFetch] = useState(false)
  const [hasOrder, setHasOrder] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [count, setCount] = useState(0)
  const [nextUrl, setNextUrl] = useState(null)
  const [prevUrl, setPrevUrl] = useState(null)
  const [pageNavLoading, setPageNavLoading] = useState(false)
  const [departmentDropdownLabel, setDepartmentDropdownLabel] =
    useState("Select Department")

  // API integration note:
  // - Old UI often worked with a plain array of tracker rows.
  // - Current endpoint contract is paginated: { count, next, previous, results }.
  // This normalizer keeps both shapes safe so older assumptions do not break.
  const normalizePaginatedData = (data) => {
    if (Array.isArray(data)) {
      return {
        count: data.length,
        next: null,
        previous: null,
        results: data,
      }
    }
    return {
      count: data?.count ?? 0,
      next: data?.next ?? null,
      previous: data?.previous ?? null,
      results: data?.results ?? [],
    }
  }

  const fetchTrackList = async ({
    department = selectedDepartment,
    level = selectedLevel,
    search = query,
  } = {}) => {
    // Query parameter contract for GET /production-tracker/items/:
    // - search: free text for item/scene lookup
    // - department: department code/value
    // - level: major|medium|minor
    // - episode_id: only for Television projects (derived from URL query)
    const params = new URLSearchParams()
    let projectType = currentUser?.project_category_type
    if (typeof projectType === "string") {
      try {
        projectType = JSON.parse(projectType)
      } catch {
        projectType = currentUser?.project_category_type
      }
    }
    if (projectType === "Television") {
      const episodeId = new URLSearchParams(location.search || "").get("episode")
      if (episodeId != null && episodeId !== "") {
        params.set("episode_id", episodeId)
      }
    }
    if (search) params.set("search", search)
    if (department) params.set("department", department)
    if (level) params.set("level", level)
    const qs = params.toString()
    const path = `/production-tracker/items/${qs ? `?${qs}` : ""}`

    try {
      let data
      if (host === CLIENT_PROGRAM_HOSTNAME) {
        const response = await axiosReq.get(path)
        if (response?.success === false) {
          throw response.error
        }
        data = response.data
        const normalizedData = normalizePaginatedData(data)
        setproTrackers(normalizedData)
        setProTrackersAll(normalizedData)
        setCount(normalizedData.count)
        setNextUrl(normalizedData.next)
        setPrevUrl(normalizedData.previous)
      } else {
        const response = await axiosInstance.get(
          `${localStorage.getItem("projectSlug")}${path}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
            withCredentials: true,
          }
        )
        if (response?.success === false) {
          throw response.error
        }
        data = response.data
        const normalizedData = normalizePaginatedData(data)
        setproTrackers(normalizedData)
        setProTrackersAll(normalizedData)
        setCount(normalizedData.count)
        setNextUrl(normalizedData.next)
        setPrevUrl(normalizedData.previous)
      }
      setHasLoaded(true)
      setHasOrder(false)
    } catch (err) {
      console.log(err)
      const backendError =
        err?.response?.data?.error ||
        "Failed to load tracker items."
      toast.error(backendError)
      if (err.response?.status !== 401) {
        setErrors(err.response?.data || { detail: [backendError] })
      }
      setCount(0)
      setNextUrl(null)
      setPrevUrl(null)
      setHasLoaded(true)
    }
  }

  const fetchTrackAll = () => {
    setHasUserTriggeredFetch(true)
    setSelectedDepartment("")
    setSelectedLevel("")
    setQuery("")
  }

  const fetchTrackByDep = (dep) => {
    setHasUserTriggeredFetch(true)
    setSelectedDepartment(dep || "")
  }

  const handleClick1 = () => {
    setHasUserTriggeredFetch(true)
    setSelectedLevel("major")
  }

  const handleClick2 = () => {
    setHasUserTriggeredFetch(true)
    setSelectedLevel("medium")
  }

  const handleClick3 = () => {
    setHasUserTriggeredFetch(true)
    setSelectedLevel("minor")
  }

  useEffect(() => {
    if (!hasUserTriggeredFetch) return
    setHasLoaded(false)
    const timer = setTimeout(() => {
      fetchTrackList({
        department: selectedDepartment,
        level: selectedLevel,
        search: query,
      })
    }, 800)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUserTriggeredFetch, query, selectedDepartment, selectedLevel])

  useEffect(() => {
    if (!hasOrder || !hasUserTriggeredFetch) return
    setHasLoaded(false)
    fetchTrackList({
      department: selectedDepartment,
      level: selectedLevel,
      search: query,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOrder, hasUserTriggeredFetch, selectedDepartment, selectedLevel, query])

  const handleNavigate = async (url) => {
    if (!url || pageNavLoading) return
    setPageNavLoading(true)
    try {
      let data
      if (host === CLIENT_PROGRAM_HOSTNAME) {
        const response = await axiosReq.get(url)
        if (response?.success === false) {
          throw response.error
        }
        data = response.data
      } else {
        const response = await axiosInstance.get(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          withCredentials: true,
        })
        if (response?.success === false) {
          throw response.error
        }
        data = response.data
      }
      const normalizedData = normalizePaginatedData(data)
      setproTrackers(normalizedData)
      setProTrackersAll(normalizedData)
      setCount(normalizedData.count)
      setNextUrl(normalizedData.next)
      setPrevUrl(normalizedData.previous)
    } catch (err) {
      console.log(err)
      toast.error("Failed to load page.")
    } finally {
      setPageNavLoading(false)
    }
  }

  const showPagination =
    hasLoaded &&
    hasUserTriggeredFetch &&
    (prevUrl != null || nextUrl != null)

  return (
    <div className={` ${styles.WhiteForm} mt-3 mb-5 mx-3`}>
      {/* title */}
      <div className={`mt-0 text-center mb-0 ${styles.SubTitle}`}>
        <span className={`float-right pt-md-1 ${styles.Close}`}
          onClick={() => setShowTracker(false)} >Close</span>
        <h5 className={`pb-0 pl-5 text-center ${styles.BoldTrack}`}>
          PRODUCTION TRACKER{" "}
        </h5>
      </div>
      <div className="px-3">
        {/* add */}
        <Row className="my-1">
          <Col xs={6} className="px-0">
            {currentUser &&
              currentUser?.groups.length > 0 &&
              (currentUser?.groups[0]?.name === "Admin" ||
                currentUser?.groups[0]?.name === "Superadmin" ||
                currentUser?.groups[0]?.name === "Admincreative") && (
                <Button
                  onClick={() => setShowAdd((showAdd) => !showAdd)}
                  className={`${btnStyles.Button} ${btnStyles.GreyDarkTrack} ml-3`}>
                  ADD TRACKER ITEM
                </Button>
              )}
          </Col>
          <Col xs={6} className="px-0">
            <Button
              className={`float-right mt-1 mr-3 px-3 ${btnStyles.GreyDark} ${btnStyles.Button}`}
              onClick={() => setShowInfo((showInfo) => !showInfo)}
            >
              INSTRUCTIONS
            </Button>
          </Col>
        </Row>
        {!showInfo ? "" : <Information />}
        {!showAdd ? (
          ""
        ) : (
          <ProTrackerCreate
            setShowAdd={setShowAdd}
            setproTrackers={setproTrackers}
          />
        )}
        <p className={`text-center py-2 ${styles.Para3Sh} `}>
          CLICK ALL ITEMS OR SELECT A DEPARTMENT FROM THE DROPDOWN TO DISPLAY TRACKER ITEMS
        </p>
        {/* filter */}
        <Row className="mt-1">
          <Col className="text-center px-0" xs={12} sm={6}>
            <Button
              className={`${btnStyles.ButtonTrack} ${btnStyles.Shed} px-3 px-md-5`}
              onClick={() => {
                setDepartmentDropdownLabel("Select Department")
                fetchTrackAll()
              }}
            >
              All Items
            </Button>
          </Col>
          <Col className="text-center pt-2 pt-sm-0" xs={12} sm={6}>
            <Dropdown className={`${styles.Add}`}>
              <Dropdown.Toggle
                variant="info"
                id="dropdown-basic"
                size="sm"
                style={{ minWidth: "150px" }}
              >
                <span>
                  {departmentDropdownLabel}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className={`pt-1 pl-2 overflow-auto`} style={{ maxHeight: "300px" }}>
                <Dropdown.Item
                  onClick={() => {
                    setDepartmentDropdownLabel("Select Department")
                    fetchTrackAll()
                  }}
                >
                  Select Department
                </Dropdown.Item>
                <Dropdown.Divider />
                {(departmentList ?? []).map(({ value, label }) => (
                  <Dropdown.Item
                    key={value}
                    onClick={() => {
                      setDepartmentDropdownLabel(label)
                      fetchTrackByDep(value)
                    }}
                  >
                    {label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>
        <p className={`text-center py-2 ${styles.Para2Sh} `}>
          After clicking All Items or selecting a Department use the tabs below to display
          Major, Medium or Minor Items
        </p>
        {/* filter */}
        <Row className="mt-1">
          <Col className="text-center px-0" xs={4}>
            <Button
              className={`py-0 ${btnStyles.Button} ${btnStyles.GreyBlue}`}
              onClick={() => handleClick1()}
            >
              Major Items
            </Button>
          </Col>
          <Col className="text-center px-0" xs={4}>
            <Button
              className={`py-0 ${btnStyles.Button} ${btnStyles.GreyBlue}`}
              onClick={() => handleClick2()}
            >
              Medium Items
            </Button>
          </Col>
          <Col className="text-center px-0" xs={4}>
            <Button
              className={`py-0 mt-1 mt-md-0 ${btnStyles.Button} ${btnStyles.GreyBlue}`}
              onClick={handleClick3}
            >
              Minor Items
            </Button>
          </Col>
        </Row>
        <p className={`text-center pt-2 ${styles.Para2Sh} `}>
          For precise search enter a value before clicking All Items or selecting a Department</p>
        {/* search setShowEdit  */}
        <Row>
          <Col className="py-2 text-center" xs={12} md={{ span: 6, offset: 3 }}>
            <Form
              className={`${styles.SearchBar}`}
              onSubmit={(event) => {
                event.preventDefault()
                if (!hasUserTriggeredFetch) {
                  setHasUserTriggeredFetch(true)
                  return
                }
                fetchTrackList({
                  department: selectedDepartment,
                  level: selectedLevel,
                })
              }}
            >
              <Form.Control
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                type="text"
                className={`${styles.InputBar}`}
                placeholder="Enter Item Name or Scene Number"
              />
            </Form>
          </Col>
        </Row>
        <hr />
        <div className="px-3">
          {/* titles */}
          <Row>
            <Col lg={{ span: 10, offset: 1 }} >
              <div className="d-none d-md-block">
                <Row style={{ textTransform: "uppercase" }}
                  className={`text-center ${styles.TitleBox}`}>
                  {/* status */}
                  <Col className={`mx-0 px-0 ${styles.TitleBox2}`} xs={1} md={1}>
                    <p className="mb-0">Status</p>
                  </Col>
                  {/* item */}
                  <Col className={`mx-0 px-0 ${styles.TitleBox2}`} xs={4} md={4}>
                    <p className="mb-0">Item Name</p>
                  </Col>
                  {/* scene number */}
                  <Col className={`mx-0 px-0 ${styles.TitleBox2}`} xs={1} md={1}>
                    <p className="mb-0">Scene</p>
                  </Col>
                  {/* Department */}
                  {currentUser &&
                    currentUser?.groups.length > 0 &&
                    (currentUser?.groups[0]?.name === "Admin" ||
                      currentUser?.groups[0]?.name === "Superadmin") ? (
                    <Col className={`mx-0 px-0 ${styles.TitleBox2}`} xs={2} md={2}>
                      <p className="mb-0">Department</p>
                    </Col>
                  ) : (
                    <Col className={`mx-0 px-0 ${styles.TitleBox2}`} xs={3} md={3}>
                      <p className="mb-0">Department</p>
                    </Col>
                  )}
                  {/* level */}
                  <Col className={`mx-0 px-0 ${styles.TitleBox2}`} xs={1} md={1}>
                    <p className="mb-0">Level</p>
                  </Col>
                  {/* status */}
                  {/* done by */}
                  <Col className={`mx-0 px-0 ${styles.TitleBox2}`} xs={2} md={2}>
                    <p className="mb-0">Done By</p>
                  </Col>
                  {/* edit */}
                  {currentUser &&
                    currentUser?.groups.length > 0 &&
                    (currentUser?.groups[0]?.name === "Admin" ||
                      currentUser?.groups[0]?.name === "Superadmin") ? (
                    <Col className={`mx-0 px-0 ${styles.TitleBox2}`} xs={1} md={1}>
                      <p className="mb-0 pl-2">Edit</p>
                    </Col>) : ("")}
                </Row>
              </div>
              <div className="d-block d-md-none">
                <Row style={{ textTransform: "uppercase" }}
                  className={`text-center ${styles.TitleBox} py-0`}>
                  {/* scene number */}
                  <Col className={`mx-0 px-0 ${styles.TitleBox2}`} xs={3}>
                    <p className="mb-0">Scene</p>
                  </Col>
                  {/* item */}
                  <Col className={`mx-0 px-0 ${styles.TitleBox2}`} xs={6}>
                    <p className="mb-0">Item Name</p>
                  </Col>
                  {/* Department */}
                  <Col className={`mx-0 px-0 ${styles.TitleBox2}`} xs={3}>
                    <p className="mb-0">Dept</p>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
          {/* items ? */}
          <Row className="mb-3">
            <Col lg={{ span: 10, offset: 1 }} >
              {proTrackers?.results?.length ? (
                proTrackers?.results?.map((tracker, index) => (
                  <ProTracker
                    style1={{
                      backgroundColor:
                        index % 2 === 0
                          ? "#fff"
                          : "#000",
                      color:
                        index % 2 === 0
                          ? "#000"
                          : "#fff",
                    }}
                    // style1={{
                    //       backgroundColor:
                    //         index % 3 === 0
                    //           ? "#dbfaf9"
                    //           : index % 2 === 0
                    //             ? "rgb(223 254 240)"
                    //             : "rgb(248 241 249)",
                    //     }}
                    tracker={tracker}
                    setHasOrder={setHasOrder}
                    setproTrackers={setproTrackers}
                    key={tracker.id} {...tracker} />
                ))
              ) : (
                hasUserTriggeredFetch && hasLoaded ? (
                  <Container className={appStyles.Content}>
                    <Asset src={NoResults} message="No Results" />
                  </Container>
                ) : (
                  <p className={`text-center my-3 ${styles.GuideText}`}>
                    Use the filters above to display Tracker Items
                  </p>
                )
              )}
            </Col>
          </Row>
          {showPagination ? (
            <Row className="mb-3">
              <Col className="d-flex justify-content-center align-items-center">
                {count > 0 ? (
                  <span className={`mr-3 ${styles.Para2Sh}`}>{count} total</span>
                ) : null}
                <Pagination className="mb-0">
                  <Pagination.Prev
                    onClick={() => handleNavigate(prevUrl)}
                    disabled={prevUrl == null || pageNavLoading}
                  />
                  <Pagination.Next
                    onClick={() => handleNavigate(nextUrl)}
                    disabled={nextUrl == null || pageNavLoading}
                  />
                </Pagination>
              </Col>
            </Row>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default ProTrackerPage
